'use client'

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { TrendingUp } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { Skeleton } from '@/components/ui/skeleton'

interface CompletionRateWidgetProps {
  completedThisWeek: number
  assignedIssues: number
  loading?: boolean
}

export function CompletionRateWidget({ completedThisWeek, assignedIssues, loading }: CompletionRateWidgetProps) {
  const t = useTranslations()

  return (
    <Card className="border h-full flex flex-col">
      <CardHeader className="pb-3 md:pb-4">
        {loading ? (
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-5 rounded-full" />
            <Skeleton className="h-5 w-32 rounded-full" />
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            <CardTitle className="text-base md:text-lg">{t('dashboard.completionRate')}</CardTitle>
          </div>
        )}
      </CardHeader>
      <CardContent className="flex-1 flex items-center">
        {loading ? (
          <div className="space-y-4 w-full">
            <div className="flex items-center justify-center">
              <Skeleton className="w-32 h-32 rounded-full" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <div className="text-center p-3 rounded-lg bg-muted/50">
                <Skeleton className="h-6 w-12 mx-auto mb-1 rounded-full" />
                <Skeleton className="h-3 w-16 mx-auto rounded-full" />
              </div>
              <div className="text-center p-3 rounded-lg bg-muted/50">
                <Skeleton className="h-6 w-12 mx-auto mb-1 rounded-full" />
                <Skeleton className="h-3 w-20 mx-auto rounded-full" />
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 w-full">
            {/* Progress circle */}
            <div className="flex items-center justify-center">
              <div className="relative w-32 h-32">
                <svg className="transform -rotate-90 w-32 h-32">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="transparent"
                    className="text-muted"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="transparent"
                    strokeDasharray={`${2 * Math.PI * 56}`}
                    strokeDashoffset={`${2 * Math.PI * 56 * (1 - (completedThisWeek / Math.max(completedThisWeek + assignedIssues, 1)))}`}
                    className="text-primary"
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-2xl font-bold">
                      {Math.round((completedThisWeek / Math.max(completedThisWeek + assignedIssues, 1)) * 100)}%
                    </div>
                    <div className="text-xs text-muted-foreground">{t('dashboard.complete')}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <div className="text-center p-3 rounded-lg bg-muted/50">
                <div className="text-lg font-bold">{completedThisWeek}</div>
                <div className="text-xs text-muted-foreground">{t('dashboard.completed')}</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-muted/50">
                <div className="text-lg font-bold">{assignedIssues}</div>
                <div className="text-xs text-muted-foreground">{t('dashboard.remaining')}</div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
